// Shared table primitives — brief §4.5 (P31, P33), 2026-09-24. The house data
// table, as the page tables (ws) adopt it:
//
//   AppTable {                         role=table, rows separate by a GAP
//     AppTableHead { columns, … }      the tonal header row
//     AppTableRow  { columns, … }      one per record
//   }
//
// Rows are Grid boxes (the MembersTable / admin table shape) carrying the ARIA
// table roles, so a row can own its radius and fill: the hover is a rounded
// capsule and the header a rounded plate, with NO stroke anywhere (§2.5: row
// separators inside rounded cards are banned — a gap and the hover tint
// separate rows; D-4). Head and rows take the SAME `columns` from the page
// and share the row font, so their em-based padding and gap line up.
//
// Contrast, computed in __tests__/tableKit.test.js from the brand values:
//   header ink on the `widgetPlate` header over the card —
//     light `inactive` (gray6) ≈ 6.4:1; dark `inactive` (gray10) ≈ 4.44:1,
//     UNDER 4.5 (the same effect D-2 found on the dark washes), so the dark
//     header takes `caption` (gray13) ≈ 8:1.
//
// A FIXED TAG on the table, the header and every row (admin audit, lane C
// 2026-09-24): DOMQL takes an element's KEY as its HTML tag when the key
// names one, so a row keyed by a record id `canvas` rendered a <canvas>
// (which paints none of its children), `form` a <form>, and a table keyed
// `Table` a real <table>. The roles cannot work on those; a div can.
//
// Every direct child of a row takes role="cell", so a control goes INSIDE a
// plain child (the cell) — a <button> made a cell loses its button role.
export const AppTable = {
  tag: 'div',
  role: 'table',
  flow: 'y',
  gap: 'W',
  width: '100%',
  minWidth: '0'
}

// The header row: a tonal PLATE and eyebrow labels (`Y`, 500, uppercase).
// `widgetPlate`, not `overlay`: a 5% `overlay` wash measured 1.00:1 on a
// common-box card (brand TabSet), `widgetPlate` steps on the sheet, the card
// and the document in both schemes.
export const AppTableHead = {
  extends: 'Grid',
  tag: 'div',
  role: 'row',
  alignItems: 'center',
  minHeight: 'controlHCompact',
  padding: '0 A',
  columnGap: 'A',
  round: 'radiusControl',
  background: 'widgetPlate',
  childProps: {
    role: 'columnheader',
    minWidth: '0',
    fontSize: 'Y',
    fontWeight: '500',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'inactive',
    '@dark': { color: 'caption' }
  }
}

// A record row: `controlH` tall, a `hover` fill, no stroke.
export const AppTableRow = {
  extends: 'Grid',
  tag: 'div',
  role: 'row',
  alignItems: 'center',
  minHeight: 'controlH',
  padding: '0 A',
  columnGap: 'A',
  round: 'radiusControl',
  color: 'title',
  ':hover': { background: 'hover' },
  childProps: { role: 'cell', minWidth: '0' }
}

// An amount or an id: the `Code` face at `Z`, right-aligned. Figures are
// tabular by the brand's html default (D-7), so a column of numbers aligns.
export const AppTableNum = {
  role: 'cell',
  fontFamily: 'Code',
  fontSize: 'Z',
  textAlign: 'right',
  justifySelf: 'end',
  whiteSpace: 'nowrap'
}
